// apps/client/src/App.tsx

import React, { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { QueryClientProvider } from '@tanstack/react-query';
import { GlobalErrorBoundary } from '@valutten/ui-common';
import AppRoutes from './routes/AppRoutes';
import { AuthProvider } from './context/AuthContext';
import { CompanyProvider } from './context/CompanyContext';
import { PostHogProviderWrapper } from './providers/PostHogProvider';
import QueryCacheLifecycle from './components/common/QueryCacheLifecycle';
import { queryClient } from './lib/queryClient';
import { db, auth } from './lib/firebase';
import { collection, addDoc } from 'firebase/firestore';
import './index.css';

const App: React.FC = () => {
  // Expose Firebase helpers for error boundary
  useEffect(() => {
    (window as any).__FIREBASE_FIRESTORE__ = { collection, addDoc };
    (window as any).__FIREBASE_AUTH__ = auth;
  }, []);

  return (
    <GlobalErrorBoundary appName="client" firebaseDb={db}>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1F2937',
            color: '#F9FAFB',
            border: '1px solid #374151',
          },
          success: {
            iconTheme: {
              primary: '#10B981',
              secondary: '#F9FAFB',
            },
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: '#F9FAFB',
            },
          },
        }}
      />
      {/* SOLE AuthProvider instance. Do NOT add another in index.tsx or elsewhere.
          Duplicate providers cause separate onAuthStateChanged listeners that
          race against invitation acceptance. See: 2026-04-10 circle-of-death fix. */}
      <AuthProvider>
        <PostHogProviderWrapper>
          <CompanyProvider>
            <QueryClientProvider client={queryClient}>
              {/* Cache-safety rules: clear on auth change, invalidate on
                  aggregation completion. Must mount app-wide, not per page. */}
              <QueryCacheLifecycle />
              <AppRoutes />
            </QueryClientProvider>
          </CompanyProvider>
        </PostHogProviderWrapper>
      </AuthProvider>
    </GlobalErrorBoundary>
  );
};

export default App;